import React, { useEffect, useRef, useState } from "react";
import { Modal, Alert, Button, Table, Row, Col } from "react-bootstrap";
import ApiManager from "../utils/ApiManager";
import SaveAltIcon from "@mui/icons-material/SaveAlt";
import ProductSearch from "./ProductSearch";
import CellSelector from "./CellSelector";
import { v4 as uuidv4 } from "uuid";
import RowCountLabel from "./RowCountLabel";
import CheckBoxComponent from "./CheckBoxComponent";
import ProductLink from "./ProductLink";

const ModalManualIncomings = ({
  movReason,
  selectedWarehouseAreaId: initialSelectedWarehouseAreaId,
  selectedWarehouseAreaDescr: initialSelectedWarehouseAreaDescr,
  selectedRack: initialSelectedRack,
  selectedCol: initialSelectedCol,
  selectedShelf: initialSelectedShelf,
  showModal,
  setShowModal,
  onSaveModal,
}) => {
  // eslint-disable-next-line no-unused-vars
  const [error, setError] = useState(null);
  const [validationError, setValidationError] = useState("");
  const [productList, setProductList] = useState([]);
  const [selectedWarehouseAreaId, setSelectedWarehouseAreaId] = useState(
    initialSelectedWarehouseAreaId
  );
  const [selectedWarehouseAreaDescr, setSelectedWarehouseAreaDescr] = useState(
    initialSelectedWarehouseAreaDescr
  );
  const [selectedRack, setSelectedRack] = useState(initialSelectedRack);
  const [selectedCol, setSelectedCol] = useState(initialSelectedCol);
  const [selectedShelf, setSelectedShelf] = useState(initialSelectedShelf);
  const warehouseAreaRef = useRef(null);
  const [suppliers, setSuppliers] = useState([]);
  const currentYear = new Date().getFullYear();
  const years = Array.from({ length: 6 }, (_, i) => currentYear - i);

  useEffect(() => {
    if (showModal) {
      setValidationError("");
      setError(null);
      ApiManager.Top.cellProducts(
        {},
        (data) => {},
        (error) => {
          setError(error);
        },
        () => {}
      );
    }
  }, [showModal]);

  useEffect(() => {
    ApiManager.Top.contacts(
      { contactType: "F" },
      (data) => {
        let contacts = data.contacts || [];
        contacts.unshift({ id: null, descr: "" });
        setSuppliers(contacts);
      },
      (error) => {
        console.error("Error fetching suppliers:", error);
      },
      () => {}
    );
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  useEffect(() => {
    // Aggiorna i valori nel CellSelector quando cambiano i props iniziali
    setSelectedWarehouseAreaId(initialSelectedWarehouseAreaId);
    setSelectedWarehouseAreaDescr(initialSelectedWarehouseAreaDescr);
    setSelectedRack(initialSelectedRack);
    setSelectedCol(initialSelectedCol);
    setSelectedShelf(initialSelectedShelf);
  }, [
    initialSelectedWarehouseAreaId,
    initialSelectedWarehouseAreaDescr,
    initialSelectedRack,
    initialSelectedCol,
    initialSelectedShelf,
  ]);

  const closeModal = () => {
    setShowModal(false);
  };

  const handleProductAdded = (product) => {
    if (
      product &&
      !productList?.some((p) => p.selected && p.id === product.id)
    ) {
      let products = [...productList];
      products.push({
        id: product.id,
        selected: true,
        productId: product.id,
        productDescr: product.descr,
        productYear: product.productYear,
        supplierId: product.supplierId,
        selectedQty: 4,
      });
      setProductList(products);
    }
  };

  // Handler for checkbox change
  const handleCheckboxChange = (rowId) => {
    const updatedRows = productList.map((row) =>
      row.id === rowId ? { ...row, selected: !row.selected } : row
    );
    setProductList(updatedRows);
  };

  const handleSupplierChange = (index, event) => {
    const newData = [...productList];
    newData[index].selectedSupplierId = event.target.value;
    setProductList(newData);
  };

  const handleYearChange = (index, event) => {
    const newData = [...productList];
    newData[index].selectedYear = event.target.value;
    setProductList(newData);
  };

  // Handler for quantity select change
  const handleQtyChange = (index, event) => {
    const newData = [...productList];
    let selectedQty = parseInt(event.target.value);
    newData[index].selectedQty = selectedQty;
    setProductList(newData);
  };

  const saveModal = () => {
    const error = validateFields();
    if (error) {
      setValidationError(error);
      return;
    }

    const updatedProducts = productList
      ?.filter((product) => product.selected)
      ?.map((product) => ({
        ...product,
        supplierId: product.selectedSupplierId,
        productYear: product.selectedYear,
        qty: product.selectedQty,
      }));

    const newGuid = uuidv4();

    if (movReason === 1) {
      ApiManager.Top.stockIncomings(
        newGuid,
        updatedProducts,
        selectedWarehouseAreaId,
        selectedWarehouseAreaDescr,
        selectedRack,
        selectedCol,
        selectedShelf,
        (data) => {
          onSaveModal(data);
          setShowModal(false);
        },
        (error) => {
          console.error("Errore durante il salvataggio:", error);
          setValidationError("Errore durante il salvataggio.");
        },
        () => {}
      );
    }
    // else if (movReason === 0) {
    //   await ApiManager.Top.stockOutgoings(
    //     null,
    //     updatedProducts,
    //     selectedWarehouseAreaId,
    //     selectedWarehouseAreaDescr,
    //     selectedRack,
    //     selectedCol,
    //     selectedShelf,
    //     (data) => {
    //       onSaveModal(data);
    //       setShowModal(false);
    //     },
    //     (error) => {
    //       console.error("Errore durante il salvataggio:", error);
    //       setValidationError("Errore durante il salvataggio.");
    //     },
    //     () => {}
    //   );
    // }
  };

  const validateFields = () => {
    if (!selectedWarehouseAreaId) {
      return "L'area del magazzino è obbligatoria.";
    }
    if (!selectedRack) {
      return "Il rack è obbligatorio.";
    }
    if (!selectedCol) {
      return "La colonna è obbligatoria.";
    }
    if (!selectedShelf) {
      return "Lo scaffale è obbligatorio.";
    }
    return "";
  };

  return (
    <Modal
      className="incomingModals"
      show={showModal}
      onHide={closeModal}
      size="lg"
      backdrop="static"
    >
      <Modal.Header closeButton>
        <strong className="header-text">
          {movReason === 1 ? "Carico" : "Scarico"}
        </strong>
      </Modal.Header>
      <Modal.Body>
        {validationError && <Alert variant="danger">{validationError}</Alert>}
        <ProductSearch onProductAdded={handleProductAdded} />
        <Row className="mb-3">
          <Col>
            <RowCountLabel
              count={productList?.length}
              label="Articoli da caricare"
            />
            <Table striped>
              <thead>
                <tr>
                  <th style={{ width: "64px" }}></th>
                  <th>Articolo</th>
                  <th>Fornitore</th>
                  <th className="text-end" style={{ width: "100px" }}>
                    Anno
                  </th>
                  <th className="text-end" style={{ width: "170px" }}>
                    Qta
                  </th>
                </tr>
              </thead>
              <tbody>
                {productList
                  // ?.filter((row) => row.selected)
                  ?.map((row, rowIndex) => (
                    <React.Fragment key={`row-${row.id}`}>
                      <tr
                        style={{ verticalAlign: "middle", fontWeight: "bold" }}
                      >
                        <td className="text-center">
                          <CheckBoxComponent
                            id={`qty-${row.id}`}
                            checked={row.selected}
                            onChange={() => handleCheckboxChange(row.id)}
                          />
                        </td>
                        <td>
                          <ProductLink {...row} showProductLink={false} />
                        </td>
                        <td>
                          <select
                            id={`supplier-${row.id}`}
                            value={row.selectedSupplierId || 0}
                            onChange={(event) =>
                              handleSupplierChange(rowIndex, event)
                            }
                            className="form-select"
                          >
                            {suppliers.map((supplier) => (
                              <option key={supplier.id} value={supplier.id}>
                                {supplier.descr}
                              </option>
                            ))}
                          </select>
                        </td>
                        <td className="text-end">
                          <select
                            id={`year-${row.id}`}
                            value={row.selectedYear || currentYear}
                            onChange={(event) =>
                              handleYearChange(rowIndex, event)
                            }
                            className="form-select"
                          >
                            {years.map((year) => (
                              <option
                                key={`year-${row.id}-${year}`}
                                value={year}
                              >
                                {year}
                              </option>
                            ))}
                          </select>
                        </td>
                        <td className="text-end">
                          <input
                            id={`qty-${row.id}`}
                            type="number"
                            value={row.selectedQty || 0}
                            onChange={(event) =>
                              handleQtyChange(rowIndex, event)
                            }
                            className="form-control text-end"
                            style={{ width: "100px" }}
                            min="0"
                            step="1"
                          />
                        </td>
                      </tr>
                    </React.Fragment>
                  ))}
              </tbody>
            </Table>
          </Col>
        </Row>
        <strong className="header-text">Ubicazione di destinazione</strong>
        <CellSelector
          idPrefix="ModalManualIncomings"
          ref={warehouseAreaRef}
          selectedWarehouseAreaId={selectedWarehouseAreaId}
          setSelectedWarehouseAreaId={setSelectedWarehouseAreaId}
          selectedWarehouseAreaDescr={selectedWarehouseAreaDescr}
          setSelectedWarehouseAreaDescr={setSelectedWarehouseAreaDescr}
          selectedRack={selectedRack}
          setSelectedRack={setSelectedRack}
          selectedCol={selectedCol}
          setSelectedCol={setSelectedCol}
          selectedShelf={selectedShelf}
          setSelectedShelf={setSelectedShelf}
          editMode={true}
        />
      </Modal.Body>
      <Modal.Footer>
        <Button
          title="Salva"
          variant="success"
          onClick={saveModal}
          disabled={
            !selectedWarehouseAreaId ||
            !selectedRack ||
            !selectedCol ||
            !selectedShelf ||
            productList?.filter((product) => product.selected)?.length === 0 ||
            productList?.some(
              (row) =>
                row.selected &&
                (!row.selectedSupplierId ||
                  !row.selectedYear ||
                  !row.selectedQty ||
                  row.selectedQty <= 0)
            )
          }
        >
          <SaveAltIcon />
        </Button>
      </Modal.Footer>
    </Modal>
  );
};

export default ModalManualIncomings;
